import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
} from '../../../utils/seoSchemas';
import { TrendingUp, AlertTriangle, Globe, BarChart3 } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const HormuzCountriesDependent = () => {
  const title = 'Countries Dependent on Strait of Hormuz | Oil Import Dependencies';
  const description =
    'Which countries depend most on oil flows through the Strait of Hormuz? Analysis of import dependencies, vulnerability, and alternative supply options.';

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Countries Dependent', path: '/strait-of-hormuz/countries-dependent' },
    ]),
  ];

  const [mapView, setMapView] = useState('importers'); // 'importers' or 'exporters'

  const exporters = [
    { country: 'Saudi Arabia', share: 35, barrels: 7.3, code: 'SAU', coords: [45, 24] },
    { country: 'Iran', share: 20, barrels: 4.2, code: 'IRN', coords: [53, 32] },
    { country: 'Iraq', share: 18, barrels: 3.8, code: 'IRQ', coords: [44, 33] },
    { country: 'UAE', share: 15, barrels: 3.1, code: 'ARE', coords: [54, 24] },
    { country: 'Kuwait', share: 12, barrels: 2.5, code: 'KWT', coords: [48, 29] },
  ];

  const importers = [
    {
      country: 'China',
      dependency: 'High',
      percentage: 45,
      description: 'Largest importer, heavily dependent on Persian Gulf oil',
      code: 'CHN',
      coords: [104, 35],
      riskLevel: 3, // 1=low, 2=medium, 3=high, 4=very high
    },
    {
      country: 'India',
      dependency: 'High',
      percentage: 60,
      description: 'Rapidly growing demand, limited alternative sources',
      code: 'IND',
      coords: [77, 20],
      riskLevel: 3,
    },
    {
      country: 'Japan',
      dependency: 'Very High',
      percentage: 80,
      description: 'Almost entirely dependent on Middle East imports',
      code: 'JPN',
      coords: [139, 36],
      riskLevel: 4,
    },
    {
      country: 'South Korea',
      dependency: 'Very High',
      percentage: 75,
      description: 'High dependency with limited strategic reserves',
      code: 'KOR',
      coords: [127, 36],
      riskLevel: 4,
    },
    {
      country: 'Singapore',
      dependency: 'Medium',
      percentage: 30,
      description: 'Refining hub, re-exports to regional markets',
      code: 'SGP',
      coords: [103, 1],
      riskLevel: 2,
    },
  ];

  // Chart data
  const exporterPieData = exporters.map(e => ({
    name: e.country,
    value: e.share,
  }));

  const importerBarData = importers.map(i => ({
    name: i.country,
    dependency: i.percentage,
    risk: i.riskLevel * 20, // Convert to 0-100 scale
  }));

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Get color based on dependency level
  const getDependencyColor = (percentage) => {
    if (percentage >= 70) return '#ef4444'; // Red - Very High
    if (percentage >= 50) return '#f97316'; // Orange - High
    if (percentage >= 30) return '#f59e0b'; // Yellow - Medium
    return '#10b981'; // Green - Low
  };

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/countries-dependent"
        keywords="countries dependent on Hormuz, Hormuz oil imports, Persian Gulf oil dependency"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-overview.png"
        imageAlt="Countries Dependent on Strait of Hormuz - Oil import dependencies"
      />

      <HormuzHubNav />

      {/* A. Overview */}
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
          Countries Dependent on the Strait of Hormuz
        </h1>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-2">
          Last updated: March 2026 · This page focuses on structural exposure to Strait of Hormuz
          flows rather than short-term market moves and is intended for informational use only.
        </p>
        <p className="text-slate-600 dark:text-on-surface-variant text-sm md:text-base leading-relaxed">
          Many major importing economies rely on crude and refined products that transit the Strait
          of Hormuz. The degree of reliance varies widely by country, depending on domestic
          production, alternative pipeline routes, strategic reserves, and the ability to switch
          suppliers. Understanding who is most exposed helps explain why some governments react more
          strongly than others when tensions rise in the Gulf.
        </p>
      </header>

      {/* D. Country Dependency Table (Snapshot) */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Country Dependency Snapshot
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-4">
          The table below groups selected importers into broad dependency tiers. Percentages are
          indicative ranges based on public estimates of how much of each country&apos;s crude and
          product imports are sourced from exporters that normally ship through the Strait of
          Hormuz. They are designed to illustrate relative exposure rather than provide exact,
          real‑time figures.
        </p>
        <div className="overflow-x-auto overflow-hidden rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container mb-2">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-100 dark:bg-surface-container-low">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Country / Region
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Approx. dependency via Hormuz
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Risk level
                </th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Japan</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">~80–90% of crude imports</td>
                <td className="px-3 py-2 font-semibold text-red-600 dark:text-red-400">Very High</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Limited domestic production and high reliance on seaborne Gulf cargoes make Japan
                  highly sensitive to any chokepoint disruption.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">South Korea</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">~70–80% of crude imports</td>
                <td className="px-3 py-2 font-semibold text-red-600 dark:text-red-400">Very High</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Large refining sector geared toward Middle Eastern grades, with strong but not
                  unlimited diversification options.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">India</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Often around ~60%</td>
                <td className="px-3 py-2 font-semibold text-orange-600 dark:text-orange-400">High</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Rapidly growing demand, important refining hubs, and persistent current‑account
                  constraints heighten vulnerability to price shocks.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">China</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">Roughly ~40–50%</td>
                <td className="px-3 py-2 font-semibold text-amber-600 dark:text-amber-400">Medium–High</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Significant Gulf exposure, partially offset by pipeline supplies and diversified
                  seaborne imports from other regions.
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">Europe (aggregate)</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  Mixed; material but less concentrated
                </td>
                <td className="px-3 py-2 font-semibold text-yellow-600 dark:text-yellow-400">Medium</td>
                <td className="px-3 py-2 text-slate-700 dark:text-on-surface-variant">
                  More diversified via North Sea, Mediterranean, and pipeline routes, but still
                  exposed to global price levels driven by Hormuz risk.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant">
          For more granular, country‑by‑country metrics and visualisation, see the interactive
          charts and global map below. These should be read together with the{' '}
          <Link
            to="/strait-of-hormuz/statistics"
            className="text-primary-600 dark:text-primary underline"
          >
            Hormuz Statistics
          </Link>{' '}
          page and the{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-600 dark:text-primary underline"
          >
            Economic Impact
          </Link>{' '}
          scenarios.
        </p>
      </section>

      {/* B. Why Dependency Matters */}
      <section className="mb-10 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          Why Dependency on Hormuz Matters for Policy and Markets
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-3">
          A country&apos;s vulnerability is not just about how many barrels it imports through the
          Strait of Hormuz. It also depends on how quickly it can switch suppliers, draw on
          strategic reserves, change refinery configurations, or pass higher costs through to
          households and industry. Two economies with similar headline dependence can experience
          very different levels of stress in a disruption.
        </p>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
          <li>
            Highly import‑dependent economies with limited domestic production are more exposed to
            both supply interruptions and price spikes.
          </li>
          <li>
            Countries with concentrated refining systems optimised for particular crude grades may
            find it harder to switch quickly to alternative barrels.
          </li>
          <li>
            Fiscal regimes and fuel subsidy structures influence how much of the shock is absorbed
            by governments versus households and firms.
          </li>
        </ul>
      </section>

      {/* World Map Visualization */}
      <section className="mb-10">
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface flex items-center">
              <Globe className="w-6 h-6 mr-2 text-primary-600" />
              Global Dependency Map
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setMapView('importers')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mapView === 'importers'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-slate-700 dark:text-on-surface-variant hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Importers
              </button>
              <button
                onClick={() => setMapView('exporters')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mapView === 'exporters'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-slate-700 dark:text-on-surface-variant hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                Exporters
              </button>
            </div>
          </div>
          <div className="h-96 w-full rounded-lg overflow-hidden bg-slate-100 dark:bg-surface-container-low">
            <ComposableMap projectionConfig={{ scale: 150 }}>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const countryCode = geo.properties.ISO_A2;
                    const isHighlighted = mapView === 'importers'
                      ? importers.some(i => i.code === countryCode)
                      : exporters.some(e => e.code === countryCode);
                    
                    let fillColor = '#e5e7eb';
                    if (isHighlighted && mapView === 'importers') {
                      const importer = importers.find(i => i.code === countryCode);
                      fillColor = getDependencyColor(importer?.percentage || 0);
                    } else if (isHighlighted && mapView === 'exporters') {
                      fillColor = '#3b82f6';
                    }
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fillColor}
                        stroke="#ffffff"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: { outline: 'none', fill: fillColor, opacity: 0.8 },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {mapView === 'importers' && importers.map((importer, idx) => (
                <Marker key={idx} coordinates={importer.coords}>
                  <circle r={6} fill={getDependencyColor(importer.percentage)} stroke="#fff" strokeWidth={2} />
                  <text
                    textAnchor="middle"
                    y={-10}
                    style={{ fontFamily: 'system-ui', fill: '#374151', fontSize: '10px', fontWeight: 'bold' }}
                  >
                    {importer.country}
                  </text>
                </Marker>
              ))}
              {mapView === 'exporters' && exporters.map((exporter, idx) => (
                <Marker key={idx} coordinates={exporter.coords}>
                  <circle r={6} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
                  <text
                    textAnchor="middle"
                    y={-10}
                    style={{ fontFamily: 'system-ui', fill: '#374151', fontSize: '10px', fontWeight: 'bold' }}
                  >
                    {exporter.country}
                  </text>
                </Marker>
              ))}
            </ComposableMap>
          </div>
          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></div>
              <span className="text-slate-600 dark:text-on-surface-variant">Very High (70%+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f97316' }}></div>
              <span className="text-slate-600 dark:text-on-surface-variant">High (50-70%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
              <span className="text-slate-600 dark:text-on-surface-variant">Medium (30-50%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
              <span className="text-slate-600 dark:text-on-surface-variant">Low (&lt;30%)</span>
            </div>
          </div>
        </div>
      </section>

      {/* Exporters */}
      <section className="mb-10">
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4 flex items-center">
          <TrendingUp className="w-6 h-6 mr-2 text-primary-600" />
          Major Oil Exporters
        </h2>
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Pie Chart */}
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4">
              Export Share Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={exporterPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {exporterPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Exporters List */}
          <div className="grid md:grid-cols-2 gap-4">
            {exporters.map((exporter, idx) => (
              <div key={idx} className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-5">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-2">
                  {exporter.country}
                </h3>
                <div className="space-y-1 text-sm">
                  <p className="text-slate-600 dark:text-on-surface-variant">
                    Share: <span className="font-medium text-slate-900 dark:text-on-surface">{exporter.share}%</span>
                  </p>
                  <p className="text-slate-600 dark:text-on-surface-variant">
                    Volume: <span className="font-medium text-slate-900 dark:text-on-surface">{exporter.barrels}M/day</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Importers */}
      <section>
        <h2 className="text-2xl font-semibold text-slate-900 dark:text-on-surface mb-4 flex items-center">
          <AlertTriangle className="w-6 h-6 mr-2 text-primary-600" />
          Major Oil Importers
        </h2>
        
        {/* Bar Chart */}
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-primary-600" />
            Dependency Levels by Country
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={importerBarData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} label={{ value: 'Dependency %', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="dependency" name="Hormuz Dependency %" fill="#3b82f6">
                {importerBarData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getDependencyColor(entry.dependency)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          {importers.map((importer, idx) => (
            <div key={idx} className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface">
                  {importer.country}
                </h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    importer.dependency === 'Very High'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : importer.dependency === 'High'
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                  }`}
                >
                  {importer.dependency} Dependency
                </span>
              </div>
              <p className="text-sm text-slate-600 dark:text-on-surface-variant mb-2">
                <strong>Hormuz dependency:</strong> {importer.percentage}% of oil imports
              </p>
              <p className="text-sm text-slate-700 dark:text-on-surface-variant">{importer.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* G. What Most Websites Don’t Tell You */}
      <section className="mt-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          What Most Websites Don&apos;t Tell You About Country Dependency
        </h2>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
          <li>
            Similar percentage dependencies can mask very different levels of resilience. A
            country with deep futures markets, diversified refining, and robust storage can manage a
            disruption much more effectively than one without those tools.
          </li>
          <li>
            Dependency is dynamic: long‑term contracts, investment in alternative pipelines, and
            changing domestic demand all shift exposure over time. Static charts rarely capture
            these underlying trends.
          </li>
          <li>
            Policy reactions can amplify or cushion the shock. For example, releasing reserves,
            adjusting fuel taxes, or temporarily relaxing product specifications can materially
            change the real‑world impact of the same chokepoint event.
          </li>
          <li>
            Financial markets often re‑price risk before physical cargoes are affected, meaning that
            highly exposed importers may experience currency and bond‑market volatility well before
            any barrels are actually delayed.
          </li>
        </ul>
        <p className="text-xs text-slate-600 dark:text-on-surface-variant mt-3">
          To see how this structural dependency translates into price and macro risk, explore the{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-700 dark:text-primary-300 underline"
          >
            Economic Impact
          </Link>{' '}
          scenarios and the live{' '}
          <Link
            to="/strait-of-hormuz/risk-dashboard"
            className="text-primary-700 dark:text-primary-300 underline"
          >
            Risk Dashboard
          </Link>
          .
        </p>
      </section>
    </article>
  );
};

export default HormuzCountriesDependent;

